// src/hooks/useStudents.js
import { useState, useEffect, useCallback } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, functions, storage } from '../firebase/config';
import { compressImage } from '../utils/imageCompress';
import { useAuth } from '../context/AuthContext';

const useStudents = () => {
  const { schoolId, role, fieldPermissions } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchStudents = useCallback(async () => {
    if (!schoolId || role === 'admin') {
      setStudents([]);
      return;
    }

    setLoading(true);
    try {
      const q = query(
        collection(db, 'students'),
        where('school_id', '==', String(schoolId))
      );

      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      data.sort((a, b) => {
        const aTime = a.created_at?.seconds || 0;
        const bTime = b.created_at?.seconds || 0;
        return bTime - aTime;
      });

      setStudents(data);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, role]);

  const uploadStudentPhoto = async (studentData, imageBlobOrFile) => {
    if (!imageBlobOrFile || !schoolId) return '';

    const compressed = await compressImage(imageBlobOrFile, {
      quality: 0.7,
      maxSide: 2000,
    });

    const storageRef = ref(
      storage,
      `schools/${schoolId}/photos/${studentData.std_roman || 'NA'}/${Date.now()}.jpg`
    );

    await uploadBytes(storageRef, compressed, {
      contentType: 'image/jpeg',
    });

    return getDownloadURL(storageRef);
  };

  const normalizeStudentPayload = (studentData, extras = {}) => {
    const payload = {
      school_id: String(schoolId),
      student_name: studentData.student_name || '',
      std_roman: studentData.std_roman || '',
      update_type: studentData.update_type || null, // അപ്‌ഡേറ്റ് ടൈപ്പ് ചേർത്തു
      originalPhotoName: studentData.originalPhotoName || '', // ഒറിജിനൽ ഫോട്ടോ നെയിം ചേർത്തു
    };

    const optionalKeys = [
      'serial_no',
      'division',
      'custom_div',
      'fathers_name',
      'dob',
      'reg_no',
      'pen_no',
      'appar_id',
      'sat_no',
      'house',
      'address',
      'mobile',
      'photo_name',
      'photo_url',
    ];

    optionalKeys.forEach((key) => {
      // update_type-ന് പെർമിഷൻ ചെക്ക് ആവശ്യമില്ലാത്തതുകൊണ്ട് നേരിട്ട് ആഡ് ചെയ്തു
      if (fieldPermissions?.[key]) {
        payload[key] = studentData[key] ?? '';
      }
    });

    return {
      ...payload,
      ...extras,
    };
  };

  const addStudent = async (studentData, imageBlobOrFile) => {
    if (!schoolId) throw new Error('School context missing.');

    try {
      const photo_url =
        imageBlobOrFile && fieldPermissions?.photo_url
          ? await uploadStudentPhoto(studentData, imageBlobOrFile)
          : studentData.photo_url || '';

      await addDoc(
        collection(db, 'students'),
        normalizeStudentPayload(studentData, {
          photo_name: fieldPermissions?.photo_name
            ? imageBlobOrFile?.name || studentData.photo_name || ''
            : '',
          photo_url: fieldPermissions?.photo_url ? photo_url : '',
          created_at: serverTimestamp(),
          updated_at: null,
        })
      );

      await fetchStudents();
    } catch (error) {
      console.error('Error adding student:', error);
      throw error;
    }
  };

  const updateStudent = async (id, studentData, imageBlobOrFile) => {
    if (!schoolId) throw new Error('School context missing.');

    try {
      const photo_url =
        imageBlobOrFile && fieldPermissions?.photo_url
          ? await uploadStudentPhoto(studentData, imageBlobOrFile)
          : studentData.photo_url || '';

      await updateDoc(
        doc(db, 'students', id),
        normalizeStudentPayload(studentData, {
          photo_name: fieldPermissions?.photo_name
            ? imageBlobOrFile?.name || studentData.photo_name || ''
            : '',
          photo_url: fieldPermissions?.photo_url ? photo_url : '',
          updated_at: serverTimestamp(),
        })
      );

      await fetchStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  };

  const deleteStudent = async (id) => {
    if (!schoolId) throw new Error('School context missing.');

    try {
      const deleteStudentCascade = httpsCallable(functions, 'deleteStudentCascade');
      await deleteStudentCascade({ studentId: id });
      await fetchStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
      throw new Error(error?.message || 'Failed to delete student.');
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { students, loading, addStudent, updateStudent, deleteStudent, fetchStudents };
};

export default useStudents;